import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Send, Paperclip, MessageCircle, Mail, Phone, Smile, Mic, MoreHorizontal, PhoneCall, CheckCircle 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ComposeMessage = ({ onSend, conversationId, customer, onCall }) => {
  const [message, setMessage] = useState('');
  const [channel, setChannel] = useState('sms');
  const [sending, setSending] = useState(false);
  const [calling, setCalling] = useState(false);
  const [providerStatus, setProviderStatus] = useState(null);
  
  // Fetch provider status on mount
  useEffect(() => {
    const fetchProviderStatus = async () => {
      try {
        const response = await axios.get(`${API}/communications/provider-status`);
        setProviderStatus(response.data);
      } catch (error) {
        console.error('Error fetching provider status:', error);
      }
    };
    fetchProviderStatus();
  }, []);
  
  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSending(true);
    try {
      const result = await onSend({
        body: message.trim(),
        channel,
        direction: 'outbound'
      });
      setMessage('');
      
      // Show provider status if SMS
      if (channel === 'sms' && result?.provider_result) {
        if (result.provider_result.success) {
          toast.success('SMS sent via CallRail');
        } else {
          toast.warning(`Message saved. ${result.provider_result.error || 'SMS delivery pending'}`);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  const handleCall = async () => {
    if (!customer?.phone) {
      toast.error('No phone number available');
      return;
    }
    
    setCalling(true);
    try {
      const response = await axios.post(
        `${API}/communications/conversations/${conversationId}/call`,
        {}
      );
      
      if (response.data.success) {
        toast.success('Call initiated!');
      } else {
        toast.error('Failed to initiate call');
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate call');
    } finally {
      setCalling(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const getChannelIcon = () => {
    switch (channel) {
      case 'sms': return <MessageCircle className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      case 'call': return <Phone className="h-4 w-4" />;
      default: return <MessageCircle className="h-4 w-4" />;
    }
  };
  
  return (
    <div className="border-t p-4">
      {/* Provider Status Badge */}
      {providerStatus && (
        <div className="flex items-center gap-2 mb-2">
          <Badge 
            variant={providerStatus.configured ? "default" : "outline"}
            className={`text-xs ${providerStatus.configured ? 'bg-green-600' : ''}`}
          >
            {providerStatus.configured ? (
              <>
                <CheckCircle className="h-3 w-3 mr-1" />
                CallRail Connected
              </>
            ) : (
              'CallRail Not Configured'
            )}
          </Badge>
          {providerStatus.tracking_number && providerStatus.configured && (
            <span className="text-xs text-muted-foreground">
              From: {providerStatus.tracking_number}
            </span>
          )}
        </div>
      )}
      
      {/* Channel Selector */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs text-muted-foreground">Reply via:</span>
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="w-[120px] h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sms">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-3 w-3" />
                SMS
              </div>
            </SelectItem>
            <SelectItem value="email">
              <div className="flex items-center gap-2">
                <Mail className="h-3 w-3" />
                Email
              </div>
            </SelectItem>
            <SelectItem value="internal">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-3 w-3" />
                Internal Note
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
        
        {customer?.phone && channel === 'sms' && (
          <span className="text-xs text-muted-foreground">
            → {customer.phone}
          </span>
        )}
        {customer?.email && channel === 'email' && (
          <span className="text-xs text-muted-foreground">
            → {customer.email}
          </span>
        )}
      </div>
      
      {/* Compose Area */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Textarea
            placeholder={`Type your ${channel === 'internal' ? 'internal note' : 'message'}...`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-[80px] resize-none pr-10"
            disabled={sending}
          />
          
          {/* Emoji Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 bottom-2 h-7 w-7"
            disabled={sending}
          >
            <Smile className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          <Button
            onClick={handleSend}
            disabled={!message.trim() || sending}
            className="h-10"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <>
                <Send className="h-4 w-4 mr-1" />
                Send
              </>
            )}
          </Button>
          
          {/* Call Button - CallRail tracks calls, shows click-to-call */}
          {customer?.phone && providerStatus?.configured && (
            <Button 
              variant="outline" 
              onClick={handleCall}
              disabled={calling}
              className="h-10 border-green-500 text-green-600 hover:bg-green-50"
            >
              {calling ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
              ) : (
                <>
                  <PhoneCall className="h-4 w-4 mr-1" />
                  Call
                </>
              )}
            </Button>
          )}
          
          <div className="flex gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={sending}>
              <Paperclip className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={sending}>
              <Mic className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="flex items-center gap-2 mt-2">
        <Button variant="ghost" size="sm" className="h-6 text-xs">
          Use Template
        </Button>
        <Button variant="ghost" size="sm" className="h-6 text-xs">
          Schedule Send
        </Button>
        <Button variant="ghost" size="sm" className="h-6 text-xs">
          <MoreHorizontal className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

export default ComposeMessage;